import React, { useState, useMemo } from 'react';
import { Search, User, MapPin, Package, TrendingUp, ChevronRight } from 'lucide-react';

function UsersList({ users, onItemClick }) {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    
    const search = searchTerm.toLowerCase();
    return users.filter(user => 
      user.username?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.phone_number?.toLowerCase().includes(search)
    );
  }, [users, searchTerm]);

  // Calculate stats for each user
  const usersWithStats = useMemo(() => {
    return filteredUsers.map(user => ({
      ...user,
      totalDeliveries: user.delivery_count || 0,
      totalOrders: user.order_count || 0,
      totalActivity: (user.delivery_count || 0) + (user.order_count || 0)
    }));
  }, [filteredUsers]);

  // Sort by activity (most active first)
  const sortedUsers = useMemo(() => {
    return [...usersWithStats].sort((a, b) => b.totalActivity - a.totalActivity);
  }, [usersWithStats]);

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'driver':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'user':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getRoleLabel = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'Admin';
      case 'driver':
        return 'Conductor';
      case 'user':
        return 'Cliente';
      default:
        return role || 'Usuario';
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-slate-200 bg-gradient-to-r from-[#B1336E]/5 to-transparent">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-[#B1336E]/10 rounded-lg">
            <User className="w-5 h-5 text-[#B1336E]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800 font-montserrat">
              Usuarios
            </h2>
            <p className="text-xs text-slate-500 font-montserrat">
              {sortedUsers.length} usuario{sortedUsers.length !== 1 ? 's' : ''} con actividad
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar usuario..."
            className="w-full pl-10 pr-4 py-2 text-sm border-2 border-slate-200 rounded-lg focus:outline-none focus:border-[#B1336E] transition-colors font-montserrat"
          />
        </div>
      </div>

      {/* Users List */}
      <div className="flex-1 overflow-y-auto">
        {sortedUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <User className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-600 mb-1 font-montserrat">
              {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios con actividad'}
            </p>
            <p className="text-xs text-slate-400 font-montserrat">
              {searchTerm ? 'Intenta con otra búsqueda' : 'Selecciona otra fecha'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {sortedUsers.map((user) => (
              <button
                key={user.id}
                onClick={() => onItemClick(user.id)}
                className="w-full p-4 hover:bg-[#B1336E]/5 transition-colors text-left group"
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    {user.profile_image ? (
                      <img
                        src={user.profile_image}
                        alt={user.username}
                        className="w-12 h-12 rounded-full object-cover border-2 border-slate-200 group-hover:border-[#B1336E] transition-colors"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#B1336E] to-[#E6B43C] flex items-center justify-center border-2 border-slate-200 group-hover:border-[#B1336E] transition-colors">
                        <span className="text-white font-bold text-lg font-montserrat">
                          {user.username?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    {/* Name and Role */}
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-800 truncate font-montserrat">
                        {user.username || 'Usuario sin nombre'}
                      </h3>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border font-montserrat ${getRoleBadgeColor(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-1 mb-2">
                      {user.email && (
                        <p className="text-xs text-slate-500 truncate font-montserrat">
                          📧 {user.email}
                        </p>
                      )}
                      {user.phone_number && (
                        <p className="text-xs text-slate-500 font-montserrat">
                          📱 {user.phone_number}
                        </p>
                      )}
                    </div>

                    {/* Activity Stats */}
                    <div className="flex items-center gap-3">
                      {user.totalDeliveries > 0 && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-[#B1336E]" />
                          <span className="text-xs font-medium text-slate-600 font-montserrat">
                            {user.totalDeliveries} entrega{user.totalDeliveries !== 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                      {user.totalOrders > 0 && (
                        <div className="flex items-center gap-1">
                          <Package className="w-3.5 h-3.5 text-[#E6B43C]" />
                          <span className="text-xs font-medium text-slate-600 font-montserrat">
                            {user.totalOrders} recojo{user.totalOrders !== 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex-shrink-0 self-center">
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#B1336E] transition-colors" />
                  </div>
                </div>

                {/* Provider Info (if applicable) */}
                {user.provider_name && (
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <div className="flex items-center gap-1.5">
                      <TrendingUp className="w-3 h-3 text-[#67A43C]" />
                      <span className="text-xs text-slate-600 font-montserrat">
                        Proveedor: <span className="font-medium">{user.provider_name}</span>
                      </span>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="flex-shrink-0 p-4 border-t border-slate-200 bg-slate-50">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-4 h-4 text-[#B1336E]" />
              <span className="text-xs font-medium text-slate-600 font-montserrat">
                Entregas
              </span>
            </div>
            <p className="text-xl font-bold text-slate-800 font-montserrat">
              {sortedUsers.reduce((sum, u) => sum + u.totalDeliveries, 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-[#E6B43C]" />
              <span className="text-xs font-medium text-slate-600 font-montserrat">
                Recojos
              </span>
            </div>
            <p className="text-xl font-bold text-slate-800 font-montserrat">
              {sortedUsers.reduce((sum, u) => sum + u.totalOrders, 0)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsersList;